import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Eye, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Collapsible sample-email preview used for the weekly Digest and the monthly
 * Newsletter blocks in `/account`. Fetches the HTML on first expand so we
 * don't hammer the backend when the section is closed.
 *
 * Props:
 *  - endpoint: string (e.g. "/me/notifications/digest/preview")
 *  - label: string (button text — default "Voir un exemple")
 *  - testId: string data-testid prefix
 */
export default function SimpleEmailPreview({ endpoint, label = "Voir un exemple", testId }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || preview) return;
    let alive = true;
    (async () => {
      setBusy(true); setErr("");
      try {
        const r = await api.get(endpoint);
        if (alive) setPreview(r.data);
      } catch {
        if (alive) setErr("Impossible de charger l'exemple.");
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [open, endpoint, preview]);

  return (
    <div className="mt-3" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#E5276A] hover:underline"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <Eye className="w-3.5 h-3.5" /> {label}
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-[#1F3A5F]/12 bg-white overflow-hidden">
          {busy && (
            <div className="py-8 text-center text-[#8B95A1]">
              <Loader2 className="w-5 h-5 animate-spin inline-block" />
            </div>
          )}
          {err && <div className="p-4 text-xs text-red-600">{err}</div>}
          {preview && (
            <iframe
              title={label}
              srcDoc={preview.html}
              sandbox=""
              className="w-full h-[520px] border-0 bg-white"
              data-testid={testId ? `${testId}-iframe` : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}
